import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { forkJoin, Subscription } from 'rxjs';
import { CardapioMinishopService } from '../../../../../services/cardapio-minishop/cardapio-minishop.service';
import { EmpresaConfigService } from '../../../../../services/configuracoes/empresa.service';
import { EmpresaConfigDTO } from '../../../../../models/configuracoes/empresa.model';
import {
  CardapioMinishopCategoriaDTO,
  CardapioMinishopProdutoDTO,
  CardapioMinishopProdutoFormDTO,
} from '../../../../../models/cardapio-minishop/cardapio-minishop.model';
import { GrupoDTO } from '../../../../../models/grupo.model';
import { ProdutosDTO } from '../../../../../models/produto.models';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-cardapio-minishop',
  templateUrl: './cardapio-minishop.component.html',
  styleUrls: ['./cardapio-minishop.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class CardapioMinishopComponent implements OnInit, OnDestroy {
  env = environment;

  // Estado principal
  categorias: CardapioMinishopCategoriaDTO[] = [];
  loading = false;

  // Busca
  termoBusca = '';

  // Dados da empresa
  configuracaoEmpresa: EmpresaConfigDTO | null = null;
  moedaSimbolo = 'BRL';
  moedaLocal = 'pt-BR';
  bannerUrl: string | null = null;
  logoUrl: string | null = null;
  nomeNegocio = '';

  // Sidebar de edicao de produto
  sidebarProdutoVisivel = false;
  produtoSelecionado: CardapioMinishopProdutoDTO | null = null;
  categoriaSelecionadaId = 0;
  salvandoProduto = false;

  // Traducoes
  texts = {
    titulo: '',
    novo_produto: '',
    nova_categoria: '',
    buscar_produtos: '',
    erro_carregar: '',
    erro_salvar: '',
    sucesso_salvar: '',
    sucesso_excluir: '',
    confirmar_excluir: '',
    erro_excluir: '',
    sucesso_criar_categoria: '',
    erro_criar_categoria: '',
    sucesso_reordenar: '',
    erro_reordenar: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cardapioService: CardapioMinishopService,
    private empresaConfigService: EmpresaConfigService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.loadData();
    this.loadEmpresaConfig();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) this.langChangeSub.unsubscribe();
  }

  // --- Carregamento de dados ---

  loadData(): void {
    this.loading = true;

    forkJoin({
      categorias: this.cardapioService.listarCategorias(),
      produtos: this.cardapioService.listarTodosProdutos(),
    }).subscribe({
      next: ({ categorias, produtos }) => {
        const grupos: GrupoDTO[] = categorias.content || [];
        const todosProdutos: ProdutosDTO[] = produtos.content || [];

        const produtosPorGrupo: { [key: number]: ProdutosDTO[] } = {};
        todosProdutos.forEach(p => {
          const gId = p.grupo_id || 0;
          if (!produtosPorGrupo[gId]) produtosPorGrupo[gId] = [];
          produtosPorGrupo[gId].push(p);
        });

        this.categorias = grupos.map((g, i) =>
          this.cardapioService.mapGrupoToCategoria(g, produtosPorGrupo[g.id!] || [], i)
        );

        this.construirFotoUrls();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private loadEmpresaConfig(): void {
    this.empresaConfigService.get('/configuracaoEmpresa').subscribe({
      next: (response) => {
        const config = response.content?.[0];
        if (!config) return;
        this.configuracaoEmpresa = config;
        this.nomeNegocio = config.fantasia || config.nome || '';

        if (config.moeda) {
          this.moedaSimbolo = config.moeda.codigoOperacao;
          this.moedaLocal = config.moeda.pais?.local?.replace('_', '-') ?? 'pt-BR';
        }

        if (config.id && config.minishopImagemDeTitulo) {
          this.bannerUrl = `${this.env.imgHost}/${config.id}/configuracao_empresa/${config.minishopImagemDeTitulo}`;
        }
        if (config.id && config.logo) {
          this.logoUrl = `${this.env.imgHost}/${config.id}/configuracao_empresa/${config.logo}`;
        }

        this.construirFotoUrls();
      },
    });
  }

  private construirFotoUrls(): void {
    const empresaId = this.configuracaoEmpresa?.id;
    if (!empresaId) return;

    this.categorias.forEach(cat => {
      cat.produtos.forEach(prod => {
        if (prod.foto && !prod.fotoUrl) {
          prod.fotoUrl = `${this.env.imgHost}/${empresaId}/item/${prod.foto}`;
        }
      });
    });
  }

  // --- Edicao de produto ---

  onEditarProduto(produto: CardapioMinishopProdutoDTO): void {
    this.produtoSelecionado = produto;
    this.categoriaSelecionadaId = produto.grupo_id || 0;
    this.sidebarProdutoVisivel = true;
  }

  onNovoProduto(categoriaId: number): void {
    this.produtoSelecionado = null;
    this.categoriaSelecionadaId = categoriaId;
    this.sidebarProdutoVisivel = true;
  }

  onSalvarProduto(form: CardapioMinishopProdutoFormDTO): void {
    this.salvandoProduto = true;

    const produtoOriginal = this.produtoSelecionado
      ? this.encontrarProdutoOriginal(this.produtoSelecionado.id!)
      : undefined;

    const dto = this.cardapioService.mapFormToProdutoDTO(
      form,
      this.categoriaSelecionadaId,
      produtoOriginal
    );

    if (this.produtoSelecionado?.id) {
      this.cardapioService.atualizarProduto(this.produtoSelecionado.id, dto).subscribe({
        next: () => {
          this.salvandoProduto = false;
          this.sidebarProdutoVisivel = false;
          this.messageService.add({ severity: 'success', summary: this.texts.sucesso_salvar, life: 3000 });
          this.loadData();
        },
        error: () => {
          this.salvandoProduto = false;
        },
      });
    } else {
      this.cardapioService.criarProduto(dto).subscribe({
        next: () => {
          this.salvandoProduto = false;
          this.sidebarProdutoVisivel = false;
          this.messageService.add({ severity: 'success', summary: this.texts.sucesso_salvar, life: 3000 });
          this.loadData();
        },
        error: () => {
          this.salvandoProduto = false;
        },
      });
    }
  }

  onCancelarEdicaoProduto(): void {
    this.sidebarProdutoVisivel = false;
    this.produtoSelecionado = null;
  }

  // --- Toggle visibilidade do produto ---

  onToggleVisibilidadeProduto(produto: CardapioMinishopProdutoDTO): void {
    const produtoOriginal = this.encontrarProdutoOriginal(produto.id!);
    if (!produtoOriginal) return;

    const dto: ProdutosDTO = {
      ...produtoOriginal,
      habilitarMiniShopping: !produto.visivel,
    };

    this.cardapioService.atualizarProduto(produto.id!, dto).subscribe({
      next: () => {
        produto.visivel = !produto.visivel;
        produto.habilitarMiniShopping = produto.visivel;
      },
    });
  }

  // --- Excluir produto ---

  onExcluirProduto(produtoId: number): void {
    this.confirmationService.confirm({
      message: this.texts.confirmar_excluir,
      accept: () => {
        this.cardapioService.excluirProduto(produtoId).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: this.texts.sucesso_excluir, life: 3000 });
            this.loadData();
          },
        });
      },
    });
  }

  // --- Reordenacao de categorias ---

  onCategoriaReordenada(event: { prevIndex: number; currIndex: number }): void {
    const { prevIndex, currIndex } = event;
    const copia = [...this.categorias];
    const [movido] = copia.splice(prevIndex, 1);
    copia.splice(currIndex, 0, movido);
    this.categorias = copia;
  }

  // --- Reordenacao de produtos ---

  onProdutoReordenado(event: { categoriaId: number; prevIndex: number; currIndex: number }): void {
    const categoria = this.categorias.find(c => c.id === event.categoriaId);
    if (!categoria) return;

    const copia = [...categoria.produtos];
    const [movido] = copia.splice(event.prevIndex, 1);
    copia.splice(event.currIndex, 0, movido);
    categoria.produtos = copia;
  }

  // --- Nova categoria ---

  onNovaCategoria(nome: string): void {
    if (!nome?.trim()) return;

    this.cardapioService.criarCategoria(nome.trim()).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: this.texts.sucesso_criar_categoria, life: 3000 });
        this.loadData();
      },
    });
  }

  // --- Editar categoria ---

  onEditarCategoria(categoria: CardapioMinishopCategoriaDTO): void {
    if (!categoria.id || !categoria.nome?.trim()) return;

    this.cardapioService.atualizarCategoria(categoria.id, { nome: categoria.nome }).subscribe({
      error: () => {
        this.loadData();
      },
    });
  }

  // --- Excluir categoria ---

  onExcluirCategoria(categoriaId: number): void {
    this.confirmationService.confirm({
      message: this.texts.confirmar_excluir,
      accept: () => {
        this.cardapioService.excluirCategoria(categoriaId).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: this.texts.sucesso_excluir, life: 3000 });
            this.loadData();
          },
        });
      },
    });
  }

  // --- Ver menu publico ---

  onVerMenu(): void {
    // Consulta o status atual (mesmo endpoint do menu lateral): com o MiniShop desabilitado o
    // backend recusa o cardapio publico, entao nao abre a aba.
    this.empresaConfigService.getStatusMiniShopping().subscribe({
      next: (status) => {
        if (!status.habilitado) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('cardapio_minishop.comum.minishop_desabilitado'),
            life: 5000,
          });
          return;
        }
        const slug = status.url || this.configuracaoEmpresa?.urlDoMiniShopping;
        if (slug) {
          window.open(`/menu/${slug}`, '_blank');
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.texts.erro_carregar,
          life: 5000,
        });
      },
    });
  }

  // --- Helper ---

  private encontrarProdutoOriginal(produtoId: number): ProdutosDTO | undefined {
    // Buscar produto original nos dados do servico (aproximacao: usar dados do cardapio)
    for (const cat of this.categorias) {
      const prod = cat.produtos.find(p => p.id === produtoId);
      if (prod) {
        return prod as unknown as ProdutosDTO;
      }
    }
    return undefined;
  }

  // --- Traducoes ---

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('cardapio_minishop.gestao.titulo'),
      novo_produto: this.translate.instant('cardapio_minishop.categorias.novo_produto'),
      nova_categoria: this.translate.instant('cardapio_minishop.gestao.nova_categoria'),
      buscar_produtos: this.translate.instant('cardapio_minishop.header.buscar_produtos'),
      erro_carregar: this.translate.instant('cardapio_minishop.mensagens.erro_carregar'),
      erro_salvar: this.translate.instant('cardapio_minishop.comum.erro_generico'),
      sucesso_salvar: this.translate.instant('cardapio_minishop.comum.sucesso'),
      sucesso_excluir: this.translate.instant('cardapio_minishop.comum.sucesso'),
      confirmar_excluir: this.translate.instant('cardapio_minishop.comum.confirmar_excluir'),
      erro_excluir: this.translate.instant('cardapio_minishop.comum.erro_generico'),
      sucesso_criar_categoria: this.translate.instant('cardapio_minishop.comum.sucesso'),
      erro_criar_categoria: this.translate.instant('cardapio_minishop.comum.erro_generico'),
      sucesso_reordenar: this.translate.instant('cardapio_minishop.comum.sucesso'),
      erro_reordenar: this.translate.instant('cardapio_minishop.comum.erro_generico'),
    };
  }
}
